import { useState, useEffect } from "react";
import { CreditCard, Clock, AlertTriangle, CheckCircle, Info, Wallet, Loader2 } from "lucide-react";
import ApiService from "../../services/api";
import Modal from "../ui/Modal";

const AdminPaymentProcessingModal = ({ invoice, client, onClose, onPaymentProcessed }) => {
  const [paymentOptions, setPaymentOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [autoDeductWallet, setAutoDeductWallet] = useState(true);
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (client && invoice) {
      setPaymentAmount((invoice.totalAmount - invoice.totalPaid).toFixed(2));
      setDescription(`Payment for invoice ${invoice.number}`);
      loadPaymentOptions();
    }
  }, [client, invoice]);

  const loadPaymentOptions = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await ApiService.getClientPaymentOptions(client._id, invoice._id);

      console.log('Payment options response:', response);
      
      if (response.success) {
        setPaymentOptions(response.data[0]);
        
        // Auto-select first payment method if available
        if (response.data.payment_methods && response.data.payment_methods.length > 0) {
          setSelectedPaymentMethod(response.data.payment_methods[0]._id.toString());
        }
      } else {
        setError('Failed to load payment options');
      }
    } catch (error) {
      console.error('Error loading payment options:', error);
      setError('Failed to load payment options');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    // Check if invoice status is payment-pending
    if (invoice.status === 'payment-pending') {
      setError('Payment is already being processed for this invoice. Please wait for completion.');
      return;
    }

    const amount = parseFloat(paymentAmount);
    const remainingBalance = invoice.totalAmount - invoice.totalPaid;
    
    if (amount > remainingBalance) {
      if (!window.confirm(`Payment amount (£${amount.toFixed(2)}) exceeds remaining balance (£${remainingBalance.toFixed(2)}). Continue anyway?`)) {
        return;
      }
    }

    try {
      setProcessing(true);
      setError('');
      
      // The API charges the invoice's outstanding balance; amount is informational.
      const response = await ApiService.processAdminPayment({
        invoiceId: invoice._id,
        amount: amount,
        autoDeductWallet: autoDeductWallet,
        paymentMethodId: selectedPaymentMethod || null,
        description: description
      });
      
      if (response.success) {
        onPaymentProcessed(response.data);
        onClose();
      } else {
        setError(response.error || 'Payment processing failed');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      setError(error.message || 'Payment processing failed');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => `£${parseFloat(amount || 0).toFixed(2)}`;

  if (loading) {
    return (
      <Modal
        isOpen={true}
        onClose={onClose}
        title="Process Payment"
        size="large"
      >
        <div className="text-center py-5">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-500" />
          <p>Loading payment options...</p>
        </div>
      </Modal>
    );
  }

  if (!paymentOptions) {
    return null;
  }

  const remainingBalance = invoice.totalAmount - invoice.totalPaid;
  const strategy = paymentOptions.recommended_strategy;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={
        <div className="flex items-center">
          <CreditCard className="w-5 h-5 mr-2" />
          Process Payment - {invoice.number}
        </div>
      }
      size="large"
      footer={
        <div className="flex gap-3">
          <button 
            type="button" 
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors" 
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className={`px-4 py-2 rounded transition-colors flex items-center ${
              invoice.status === 'payment-pending' 
                ? 'bg-yellow-500 hover:bg-yellow-600' 
                : 'bg-green-500 hover:bg-green-600'
            } text-white disabled:bg-gray-400 disabled:cursor-not-allowed`}
            onClick={handleProcessPayment}
            disabled={processing || invoice.status === 'payment-pending' || (!selectedPaymentMethod && paymentOptions.wallet_balance === 0) || !paymentAmount || parseFloat(paymentAmount) <= 0}
            title={invoice.status === 'payment-pending' ? 'Payment is already being processed' : ''}
          >
            {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {invoice.status === 'payment-pending' ? (
              <>
                <Clock className="w-4 h-4 mr-1" />
                Payment Processing
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-1" />
                Process Payment
              </>
            )}
          </button>
        </div>
      }
    >
      <div className="p-0">
            {invoice.status === 'payment-pending' && (
              <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded mb-4">
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <div>
                    <strong>Payment Processing:</strong> A payment is already being processed for this invoice. 
                    Please wait 1-3 business days for GoCardless payment to complete before processing another payment.
                  </div>
                </div>
              </div>
            )}
            
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <div className="flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  {error}
                </div>
              </div>
            )}

            {/* Client & Invoice Info */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h6 className="text-sm font-semibold text-gray-900 mb-0">Client Information</h6>
                </div>
                <div className="p-4">
                  <div className="mb-2">
                    <div className="font-semibold text-gray-900">{paymentOptions.client.name}</div>
                  </div>
                  <div className="text-sm text-gray-500">
                    {paymentOptions.client.email}
                  </div>
                </div>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h6 className="text-sm font-semibold text-gray-900 mb-0">Invoice Summary</h6>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Total</div>
                      <div className="font-bold text-gray-900">{formatCurrency(invoice.totalAmount)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Paid</div>
                      <div className="font-bold text-green-600">{formatCurrency(invoice.totalPaid)}</div>
                    </div>
                    <div className="col-span-2 mt-2">
                      <div className="text-xs text-gray-500 mb-1">Outstanding</div>
                      <div className="font-bold text-yellow-600">{formatCurrency(remainingBalance)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Wallet Information */}
            {paymentOptions.wallet_balance > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg mb-6">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h6 className="text-sm font-semibold text-gray-900 mb-0 flex items-center">
                    <Wallet className="w-5 h-5 mr-2" />
                    Wallet Balance
                  </h6>
                </div>
                <div className="p-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <div className="flex justify-between items-center">
                        <span>Available Balance:</span>
                        <span className="font-bold text-green-600">{formatCurrency(paymentOptions.wallet_balance)}</span>
                      </div>
                      {strategy.type === 'wallet_only' && (
                        <div className="text-green-600 text-sm mt-1 flex items-center">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Full payment available from wallet
                        </div>
                      )}
                      {strategy.type === 'hybrid' && (
                        <div className="text-blue-600 text-sm mt-1 flex items-center">
                          <Info className="w-4 h-4 mr-1" />
                          Partial payment available ({formatCurrency(strategy.wallet_amount)})
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center">
                        <input
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          type="checkbox"
                          id="autoDeductWallet"
                          checked={autoDeductWallet}
                          onChange={(e) => setAutoDeductWallet(e.target.checked)}
                        />
                        <label className="ml-2 text-sm font-medium text-gray-900" htmlFor="autoDeductWallet">
                          Auto-deduct from wallet
                        </label>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Use wallet balance first
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Methods */}
            {paymentOptions.payment_methods && paymentOptions.payment_methods.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg mb-6">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h6 className="text-sm font-semibold text-gray-900 mb-0 flex items-center">
                    <CreditCard className="w-5 h-5 mr-2" />
                    Saved Payment Methods
                  </h6>
                </div>
                <div className="p-4">
                  {paymentOptions.payment_methods.map(method => (
                    <div key={method._id} className="flex items-center mb-4 last:mb-0">
                      <input
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
                        type="radio"
                        name="paymentMethod"
                        id={`method_${method._id}`}
                        value={method._id}
                        checked={selectedPaymentMethod === method._id.toString()}
                        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                      />
                      <label className="ml-3 flex-1 flex justify-between items-center cursor-pointer" htmlFor={`method_${method._id}`}>
                        <div>
                          <div className="font-medium text-gray-900">{method.display_name}</div>
                          <div className="text-sm text-gray-500 flex items-center">
                            {method.provider === 'gocardless' ? 'Direct Debit' : 'Card Payment'}
                            {method.is_default && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Default
                              </span>
                            )}
                          </div>
                        </div>
                        <div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            method.mandate_status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {method.mandate_status === 'active' ? 'Active' : method.mandate_status}
                          </span>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Form */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h6 className="text-sm font-semibold text-gray-900 mb-0">Payment Details</h6>
              </div>
              <div className="p-4">
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Amount *</label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 text-sm text-gray-900 bg-gray-200 border border-r-0 border-gray-300 rounded-l-md">
                        £
                      </span>
                      <input
                        type="number"
                        className="rounded-none rounded-r-lg bg-gray-50 border text-gray-900 focus:ring-blue-500 focus:border-blue-500 block flex-1 min-w-0 w-full text-sm border-gray-300 p-2.5"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min="0.01"
                        required
                      />
                      <button
                        type="button"
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => setPaymentAmount(remainingBalance.toFixed(2))}
                        disabled={remainingBalance <= 0}
                      >
                        Full
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <input
                      type="text"
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Payment description"
                    />
                  </div>
                </div>

                {/* Payment Strategy Preview */}
                {/* {paymentAmount && parseFloat(paymentAmount) > 0 && (
                  <div className="bg-blue-100 border border-blue-400 text-blue-800 px-4 py-3 rounded">
                    <div className="font-semibold">Payment Strategy:</div>
                    <div className="mt-1">
                      {strategy.type === 'wallet_only' ? (
                        <span>Full payment from wallet: {formatCurrency(paymentAmount)}</span>
                      ) : strategy.type === 'hybrid' ? (
                        <div>
                          Wallet: {formatCurrency(Math.min(paymentOptions.wallet_balance, parseFloat(paymentAmount)))}
                          <br />
                          Payment Method: {formatCurrency(Math.max(0, parseFloat(paymentAmount) - paymentOptions.wallet_balance))}
                        </div>
                      ) : (
                        <span>Payment method: {formatCurrency(paymentAmount)}</span>
                      )}
                    </div>
                  </div>
                )} */}
              </div>
            </div>

            {/* No Payment Methods Warning */}
            {(!paymentOptions.payment_methods || paymentOptions.payment_methods.length === 0) && paymentOptions.wallet_balance === 0 && (
              <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded mt-4">
                <div className="flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  <div>
                    <div className="font-semibold">No automatic payment methods available.</div>
                    <div>This client has no saved payment methods or wallet balance. You'll need to process payment manually.</div>
                  </div>
                </div>
              </div>
            )}
      </div>
    </Modal>
  );
};

export default AdminPaymentProcessingModal;